import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';
import { clearWorkspaceSession, isAcademyCoordinator } from '../utils/coordinatorAccess';
import AcademyCoordinator from '../pages/AcademyCoordinator';
import '../pages/AcademyCoordinator.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
function SignOut() {
  const leave=async()=>{try{await fetch(`${API}/logout`,{method:'POST',headers:getAuthHeaders()});}finally{clearWorkspaceSession();window.location.href='/login';}};
  return <button type="button" onClick={leave}>Sign out</button>;
}

export function PasswordChange({ onSaved }) {
  const [form, setForm] = useState({ current_password:'', password:'', password_confirmation:'' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async event => {
    event.preventDefault(); setError(''); setSaving(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, { method:'POST', headers:getAuthHeaders({'Content-Type':'application/json'}), body:JSON.stringify(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.errors ? Object.values(data.errors).flat().join(' ') : data.message || 'Password could not be changed.');
      localStorage.setItem('user', JSON.stringify(data.user)); onSaved(data.user);
    } catch (failure) { setError(failure.message); } finally { setSaving(false); }
  };
  return <div className="coordinator"><div className="coord-main" style={{maxWidth:620,paddingTop:70}}><section className="coord-card"><h1>Choose your password</h1><p className="coord-muted">Replace your temporary password before opening your workspace.</p><form onSubmit={submit} style={{marginTop:24}}>{[['current_password','Temporary password'],['password','New password'],['password_confirmation','Confirm new password']].map(([key,label])=><label key={key} style={{display:'block',marginBottom:18}}><span>{label}</span><input required minLength={key==='current_password'?undefined:8} type="password" autoComplete={key==='current_password'?'current-password':'new-password'} value={form[key]} onChange={e=>setForm({...form,[key]:e.target.value})}/></label>)}{error&&<p className="coord-error" role="alert">{error}</p>}<button className="coord-primary" disabled={saving}>{saving?'Saving…':'Save password & continue'}</button></form><div style={{marginTop:16}}><SignOut/></div></section></div></div>;
}

export default function SessionGate({ children }) {
  const location = useLocation();
  const publicPage = ['/login','/register'].includes(location.pathname) || location.pathname.startsWith('/verify-certificate/');
  const token = localStorage.getItem('token');
  const [session,setSession] = useState({loading:true,user:null,error:''});
  const [retry,setRetry] = useState(0);
  useEffect(()=>{
    if(publicPage) return;
    if(!token) { setSession({loading:false,user:null,error:''}); return; }
    let active=true;
    setSession({loading:true,user:null,error:''});
    fetch(`${API}/auth/me`,{headers:getAuthHeaders()}).then(async response=>{
      const data=await response.json();
      if(!active || localStorage.getItem('token')!==token) return null;
      if(response.status===401) { clearWorkspaceSession(); return null; }
      if(!response.ok) throw new Error(data.message || 'Your access could not be checked.');
      if(!data.user?.role) throw new Error('Your account details could not be loaded.');
      localStorage.setItem('user',JSON.stringify(data.user)); return data.user;
    }).then(user=>{if(active)setSession({loading:false,user,error:''});}).catch(error=>{if(active)setSession({loading:false,user:null,error:error.message});});
    return()=>{active=false;};
  },[token,publicPage,retry]);
  if(publicPage) return children;
  if(session.loading) return <div className="coord-loading" role="status">Opening your workspace…</div>;
  if(session.error) return <div className="coordinator"><div className="coord-main"><p role="alert" className="coord-error">{session.error}</p><button onClick={()=>setRetry(v=>v+1)}>Try again</button> <SignOut/></div></div>;
  if(!session.user) return <Navigate to="/login" replace/>;
  if(session.user.role==='pending') return <div className="coordinator"><div className="coord-main"><h1>Account awaiting approval</h1><p className="coord-muted">A superadmin needs to approve your access before you can open a workspace.</p><SignOut/></div></div>;
  if(session.user.must_change_password) return <PasswordChange onSaved={user=>setSession({loading:false,user,error:''})}/>;
  if(isAcademyCoordinator(session.user)) {
    if(location.pathname!=='/coordinator') return <Navigate to="/coordinator" replace/>;
    return <AcademyCoordinator user={session.user}/>;
  }
  if(location.pathname==='/coordinator') return <Navigate to="/" replace/>;
  return children;
}
